import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { DynamicBaseClass } from '../dynamic-base-class/dynamic-base-class.component';
import { distinctUntilChanged } from 'rxjs';
import isEqual from 'lodash-es/isEqual';

type TargetLabelPosition = {
    x: number;
    y: number;
};

type TargetLabel = {
    ringNumber: number;
    textColor: string;
    backgroundColor: string;
    position: TargetLabelPosition;
};

type TargetShot = {
    id: number;
    x: number;
    y: number;
    displayIndex: number;
    opacity: number;
};

type TargetZoomLevel = {
    size: number;
    scale: number;
};

@Component({
    standalone: false,
    selector: 'app-dynamic-target',
    templateUrl: './target.component.html',
    styleUrls: ['./target.component.scss']
})
export class TargetComponent extends DynamicBaseClass implements OnInit, OnDestroy {
    readonly centerX: number = 500;
    readonly centerY: number = 500;
    readonly viewBoxSize: number = 1000;
    // 4 fixed zoom steps for dynamicScale: x2.86, x2.00, x1.43, x1.00
    readonly zoomLevels: TargetZoomLevel[] = [
        { size: 350, scale: this.viewBoxSize / 350 },
        { size: 500, scale: this.viewBoxSize / 500 },
        { size: 700, scale: this.viewBoxSize / 700 },
        { size: 1000, scale: this.viewBoxSize / 1000 }
    ];

    title: string = '';
    infoText: string = '';
    showControls: boolean = true;
    dynamicScale: boolean = false;
    shotRadius: number = 20;
    ringWidth: number = 10;
    scaleFactor: number = 5;
    currentTargetScale: number = 1;
    width: number = this.viewBoxSize;
    height: number = this.viewBoxSize;

    shotX: number = 10;
    shotY: number = -20;

    rings: number[] = [];
    labels: TargetLabel[] = [];
    shots: TargetShot[] = [];

    private nextShotId: number = 1;

    constructor(injector: Injector) {
        super(injector);
    }

    ngOnInit(): void {
        if (this.config) {
            this.title = this.config.title ?? this.title;
            this.infoText = this.config.infoText ?? this.infoText;
            this.showControls = this.config.showControls ?? this.showControls;
            this.dynamicScale = this.config.dynamicScale ?? this.dynamicScale;
            this.shotRadius = this.toNumber(this.config.shotRadius, this.shotRadius);
            this.ringWidth = this.toNumber(this.config.ringWidth, this.ringWidth);
            this.scaleFactor = this.toNumber(this.config.scaleFactor, this.scaleFactor);
            this.shotX = this.toNumber(this.config.initialShotX, this.shotX);
            this.shotY = this.toNumber(this.config.initialShotY, this.shotY);
        }

        this.rings = this.buildRings();
        this.labels = this.buildLabels();

        const hasBoundShotData = Boolean(this.config?.sourceStoreData || this.config?.storeData);
        if (hasBoundShotData) {
            this.subscription$.add(
                this.GetValueFromConfig()
                    .pipe(distinctUntilChanged((previous, current) => isEqual(previous, current)))
                    .subscribe((shots: unknown) => {
                        this.syncShotsFromSource(shots);
                    })
            );
        } else {
            this.syncShotsFromSource(this.config?.exampleShots ?? []);
        }

        super.GlobalConfig();
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
    }

    addNewShot(): void {
        this.addShot(this.shotX, this.shotY);
    }

    addShot(x: number, y: number): void {
        const normalizedShot = {
            x: this.toNumber(x, 0),
            y: this.toNumber(y, 0)
        };

        const nextShots = [
            ...this.shots.map((shot) => ({ x: shot.x, y: shot.y })),
            normalizedShot
        ];

        this.applyShots(nextShots);
        this.persistShots(nextShots);
    }

    clearShots(): void {
        this.applyShots([]);
        this.persistShots([]);
    }

    getLabelRectX(position: TargetLabelPosition): number {
        return position.x - 12;
    }

    getLabelRectY(position: TargetLabelPosition): number {
        return position.y - 10;
    }

    getShotSvgX(shot: TargetShot): number {
        return this.centerX + (shot.x * this.scaleFactor);
    }

    getShotSvgY(shot: TargetShot): number {
        return this.centerY - (shot.y * this.scaleFactor);
    }

    get targetScaleTransform(): string {
        return `translate(${this.centerX} ${this.centerY}) scale(${this.currentTargetScale}) translate(${-this.centerX} ${-this.centerY})`;
    }

    private buildRings(): number[] {
        return Array.from({ length: 10 }, (_, index) => 10 - index);
    }

    private buildLabels(): TargetLabel[] {
        const labels: TargetLabel[] = [];

        for (let ringNumber = 1; ringNumber <= 10; ringNumber++) {
            const radius = (11 - ringNumber) * this.ringWidth * this.scaleFactor;
            const textColor = ringNumber <= 6 ? 'black' : 'white';
            const backgroundColor = ringNumber <= 6 ? 'white' : 'black';

            const positions: TargetLabelPosition[] = [
                { x: this.centerX, y: this.centerY - radius },
                { x: this.centerX, y: this.centerY + radius },
                { x: this.centerX - radius, y: this.centerY },
                { x: this.centerX + radius, y: this.centerY }
            ];

            for (const position of positions) {
                labels.push({
                    ringNumber,
                    textColor,
                    backgroundColor,
                    position
                });
            }
        }

        return labels;
    }

    private syncShotsFromSource(sourceShots: unknown): void {
        const normalizedShots = Array.isArray(sourceShots) ? sourceShots : [];
        this.applyShots(normalizedShots);
    }

    private applyShots(rawShots: Array<{ x?: unknown; y?: unknown }>): void {
        this.shots = rawShots.map((shot, index, allShots) => ({
            id: index + 1,
            x: this.toNumber(shot?.x, 0),
            y: this.toNumber(shot?.y, 0),
            displayIndex: index + 1,
            opacity: this.calculateOpacity(index + 1, allShots.length)
        }));

        this.updateTargetScale();
        this.nextShotId = this.shots.length + 1;
    }

    private updateTargetScale(): void {
        if (!this.dynamicScale || this.shots.length === 0) {
            this.currentTargetScale = 1;
            return;
        }

        const requiredRadius = this.getRequiredRadiusForShots();
        const requiredSize = requiredRadius * 2;
        const selectedZoomLevel = this.pickZoomLevel(requiredSize);
        this.currentTargetScale = selectedZoomLevel.scale;
    }

    private getRequiredRadiusForShots(): number {
        const shotPadding = 16;
        const maxDistance = this.shots.reduce((largestDistance, shot) => {
            const distance = Math.hypot(shot.x * this.scaleFactor, shot.y * this.scaleFactor);
            return Math.max(largestDistance, distance + this.shotRadius + shotPadding);
        }, 0);

        return Math.max(maxDistance, this.shotRadius + shotPadding);
    }

    private pickZoomLevel(requiredSize: number): TargetZoomLevel {
        for (const zoomLevel of this.zoomLevels) {
            if (zoomLevel.size >= requiredSize) {
                return zoomLevel;
            }
        }

        return this.zoomLevels[this.zoomLevels.length - 1];
    }

    private persistShots(shots: Array<{ x: number; y: number }>): void {
        if (this.config?.targetStoreData) {
            this.dynamicUiServices.SetValue(this.config.targetStoreData, this.storeDataChildNames, shots);
            return;
        }

        if (this.config?.storeData) {
            this.dynamicUiServices.SetValue(this.config.storeData, this.storeDataChildNames, shots);
        }
    }

    private calculateOpacity(index: number, total: number): number {
        if (total <= 0) {
            return 1;
        }

        return 0.3 + (index / total) * 0.7;
    }

    private toNumber(value: unknown, fallbackValue: number): number {
        const parsedValue = Number(value);
        return Number.isFinite(parsedValue) ? parsedValue : fallbackValue;
    }
}